import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import type { ConversationSummary } from '@shared/services'
import {
  readReviewCounts,
  arrangeBy,
  deleteConversation,
  listConversations,
  listPinTimes,
  mergeSubset,
  pinKey,
  readSkillOrder,
  saveSkillOrder,
  setPin,
  withRetry,
} from '@shared/services'
import type { SkillDefinition } from '@shared/types'
import { useDragOrder } from '@shared/hooks/useDragOrder'
import { useRemembered } from '../useRemembered'
import { useAuth } from '@shared/state/AuthContext'
import { useSkills } from '@shared/hooks/useSkills'
import { spectrumColor } from '@shared/data/brandColors'
import { GooeyBackground } from '../components/GooeyBackground'
import { ChatLauncher } from '../components/ChatLauncher'
import { ProjectTile } from '../components/ProjectTile'
import { ShareDialog } from '../components/ShareDialog'
import { SkillTile } from '../components/SkillTile'
import { CloseMark } from '../components/CloseMark'
import { CardPaint, cardPicture, isCardColour, tripleOf } from '../components/CardPaint'
import { deleteProjectFile, listProjectFiles, uploadProjectFile } from '@shared/services/projectFileService'
import { readAllowances } from '@shared/services/allowanceService'
import { ALLOWANCES_ROUTE } from './AllowancesPage'
import '../overview.css'
import { BOOK_GLYPH, SkillGlyph } from '@shared/components/SkillGlyph'

type Tab = 'skills' | 'projects'

const TABS: { id: Tab; label: string }[] = [
  { id: 'skills', label: 'Tools' },
  { id: 'projects', label: 'Projects' },
]

/**
 * Which skills the grid shows (Huanrui, 2026-10-13; was All / Research /
 * Design): Starter is the person's own (since 2026-10-19; see DEFAULT_STARTER);
 * Advance is every tool that is not an administrator's; Admin is the
 * administrators' tools, and only an administrator can open it.
 */
type Area = 'starter' | 'advance' | 'admin'

const AREAS: { id: Area; label: string }[] = [
  { id: 'starter', label: 'Starter' },
  { id: 'advance', label: 'Advance' },
  { id: 'admin', label: 'Admin' },
]

/**
 * Starter's skills, by where they open (Amazon added 2026-10-13; replaced by
 * the External Library, which holds it, 2026-10-14).
 */
const STARTER_ROUTES = new Set(['/generate', '/external-library', '/internal-library'])

/**
 * Starter is each person's own since 2026-10-19 (Huanrui): the three above to
 * begin with -- Video Generation taken out -- then a + card adds any card from
 * Advance, and a card's × takes it off. Kept with the account
 * (useRemembered). The first entry DEFAULT_STARTER means "not changed yet".
 */
const DEFAULT_STARTER = '*default*'

/**
 * Not shown as cards of their own: Amazon is a source of the External
 * Library now (2026-10-14). Its row stays in the skills table -- the Worker
 * reads who may use Amazon from it -- and its old address leads here.
 */
const FOLDED_ROUTES = new Set(['/amazon'])

/**
 * Internal Library (2026-10-13): every library in one search, a card in
 * Starter and (since 2026-10-14) Advance. Defined here rather than in the skills table.
 */
const INTERNAL_LIBRARY_SKILL = {
  id: 'web-internal-library',
  slug: 'internal-library',
  name: 'Internal Library',
  summary: 'Search Audit, Visual Language and Design References at once',
  category: 'analyse',
  status: 'available',
  glyph: BOOK_GLYPH,
  inputs: [],
  usesPaidApi: false,
  route: '/internal-library',
  area: 'research',
} as SkillDefinition

/**
 * External Library (2026-10-14): Amazon, Google Images and Unsplash behind
 * one search. In Starter and Advance alike. Defined here rather than in the
 * skills table, like Internal Library.
 */
const EXTERNAL_LIBRARY_SKILL = {
  id: 'web-external-library',
  slug: 'external-library',
  name: 'External Library',
  summary: 'Search Amazon and Google Images, and send what you pick to Miro or a project',
  category: 'analyse',
  status: 'available',
  glyph: '◎',
  inputs: [],
  usesPaidApi: true,
  route: '/external-library',
  area: 'research',
} as SkillDefinition

/**
 * Allowances (2026-10-19): how much each person may spend a month, an
 * administrator's card of its own (AllowancesPage.tsx).
 */
const ALLOWANCES_SKILL = {
  id: 'web-allowances',
  slug: 'allowances',
  name: 'Allowances',
  summary: 'Set how much each person may spend a month, and answer requests for more',
  category: 'analyse',
  status: 'available',
  glyph: '◔',
  inputs: [],
  usesPaidApi: false,
  route: ALLOWANCES_ROUTE,
  area: 'tool',
  adminOnly: true,
} as SkillDefinition

/** Not shown anywhere for now (Huanrui, 2026-10-19: later): 2x2 Map. */
const HIDDEN_SLUGS = new Set(['two-by-two-map'])

const inArea = (skill: SkillDefinition, area: Area, starter: Set<string>): boolean =>
  area === 'starter'
    ? starter.has(skill.id)
    : // Internal Library is in Advance too since 2026-10-14.
      area === 'admin'
      ? skill.adminOnly === true
      : skill.adminOnly !== true

/** When it was last spoken in, in as few words as will do. */
function when(iso: string): string {
  const then = new Date(iso)
  if (Number.isNaN(then.getTime())) return ''
  const minutes = Math.floor((Date.now() - then.getTime()) / 60000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes} min ago`
  if (minutes < 60 * 24) return `${Math.floor(minutes / 60)} h ago`
  if (minutes < 60 * 24 * 7) return `${Math.floor(minutes / (60 * 24))} d ago`
  return then.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
}

/** A project card's look: its colour, and the project file shown behind it. */
interface ProjectLook {
  colour?: string
  picture?: string
}

/** Only well-formed looks from what was kept: it comes back from storage. */
function cleanLooks(value: unknown): Record<string, ProjectLook> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {}
  const out: Record<string, ProjectLook> = {}
  for (const [id, raw] of Object.entries(value as Record<string, unknown>)) {
    if (!raw || typeof raw !== 'object') continue
    const look = raw as Record<string, unknown>
    const kept: ProjectLook = {}
    if (isCardColour(look.colour)) kept.colour = look.colour
    if (typeof look.picture === 'string' && /^[0-9a-f-]{8,64}$/i.test(look.picture)) kept.picture = look.picture
    // An empty one is kept too: the look a shared copy came with, taken off.
    out[id] = kept
  }
  return out
}

/** Only well-formed colours from what was kept: it comes back from storage. */
function cleanColours(value: unknown): Record<string, string> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {}
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).filter(
      (entry): entry is [string, string] => isCardColour(entry[1]),
    ),
  )
}

export function OverviewPage() {
  /**
   * Which half is showing — kept in the address rather than in state.
   *
   * Leaving a project used to land on Skills, whichever half you had come
   * from, because the page had no way of knowing: the choice lived in a
   * variable that died with it. Written down, it survives leaving, coming
   * back, a refresh, and a link sent to somebody else. `replace` so that
   * flicking between the two does not fill the browser's own Back button with
   * a trail of tab changes.
   */
  const [params, setParams] = useSearchParams()
  const tab: Tab = params.get('tab') === 'projects' ? 'projects' : 'skills'
  const setTab = (next: Tab) =>
    setParams(next === 'skills' ? {} : { tab: next }, { replace: true })
  const { user } = useAuth()

  /**
   * The person's conversations.
   *
   * `null` while it is still being found out, which is not the same as an
   * empty list and must not draw the same thing: this panel used to say "No
   * projects yet" permanently, because it never asked. A conversation was
   * created, saved and answered, and the only place it appeared was the
   * database.
   */
  const { skills: allSkills, failed: skillsFailed, reload: reloadSkills } = useSkills()

  /** Pinned projects, each with when it was pinned. */
  const [pins, setPins] = useState<Map<string, string>>(() => new Map())
  const [chosenArea, setArea] = useRemembered<Area>('ww.overview.level', 'starter')
  /** Where this person has dragged the skill cards; null until read. */
  const [savedOrder, setSavedOrder] = useState<string[] | null>(null)
  const [projects, setProjects] = useState<ConversationSummary[] | null>(null)
  /** The project whose share dialog is open. */
  const [sharing, setSharing] = useState<ConversationSummary | null>(null)
  const [projectsFailed, setProjectsFailed] = useState(false)
  const [projectsAttempt, setProjectsAttempt] = useState(0)

  useEffect(() => {
    let cancelled = false
    withRetry(() => listPinTimes())
      .then((found) => {
        if (!cancelled) setPins(found)
      })
      .catch(() => {
        /* nothing is pinned as far as this page is concerned; it still works */
      })
    // The skill grid waits for this, so it does not draw once and then jump.
    withRetry(() => readSkillOrder('home'))
      .then((found) => {
        if (!cancelled) setSavedOrder(found)
      })
      .catch(() => {
        if (!cancelled) setSavedOrder([])
      })
    return () => {
      cancelled = true
    }
  }, [])

  /**
   * Pin or unpin, on screen at once and in the database behind it.
   *
   * Moved first and saved second, because the answer to "did that work" is
   * almost always yes and a card that waits for the server before moving feels
   * broken. If the save fails it is put back, which is the one case where the
   * optimism has to be paid for.
   */
  const togglePin = useCallback((ref: string) => {
    const key = pinKey('project', ref)
    let nowPinned = false
    let pinnedAt = ''
    setPins((prev) => {
      const next = new Map(prev)
      nowPinned = !next.has(key)
      if (nowPinned) next.set(key, new Date().toISOString())
      else {
        pinnedAt = next.get(key) ?? ''
        next.delete(key)
      }
      return next
    })
    void setPin('project', ref, nowPinned).catch(() => {
      setPins((prev) => {
        const back = new Map(prev)
        if (nowPinned) back.delete(key)
        else back.set(key, pinnedAt)
        return back
      })
    })
  }, [])

  // Fetched when the tab is first opened rather than on mount: most visits are
  // for the skills, and this is a request nobody asked for until then.
  useEffect(() => {
    if (tab !== 'projects') return
    let cancelled = false
    setProjectsFailed(false)
    withRetry(() => listConversations())
      .then((rows) => {
        if (!cancelled) setProjects(rows)
      })
      .catch(() => {
        if (!cancelled) setProjectsFailed(true)
      })
    return () => {
      cancelled = true
    }
  }, [tab, projectsAttempt])

  // Projects open in tabs of their own now, so this page is often come back
  // to after one was made or renamed elsewhere: the list is read again then.
  useEffect(() => {
    if (tab !== 'projects') return
    const onBack = () => {
      if (document.visibilityState === 'visible') setProjectsAttempt((n) => n + 1)
    }
    document.addEventListener('visibilitychange', onBack)
    return () => document.removeEventListener('visibilitychange', onBack)
  }, [tab])

  /**
   * Skills that need a Miro board are not shown here, and administrator tools
   * are not shown to anyone else — a tile that leads to a locked page is worse
   * than no tile, because it reads as something broken rather than something
   * that is not yours.
   */
  const visibleSkills = useMemo(
    () =>
      [
        ...(allSkills ?? []).filter(
          (skill) =>
            !skill.panelOnly &&
            (!skill.adminOnly || user?.role === 'admin') &&
            !FOLDED_ROUTES.has(skill.route ?? '') &&
            !HIDDEN_SLUGS.has(skill.slug),
        ),
        ...(allSkills ? [INTERNAL_LIBRARY_SKILL, EXTERNAL_LIBRARY_SKILL] : []),
        ...(allSkills && user?.role === 'admin' ? [ALLOWANCES_SKILL] : []),
      ],
    [allSkills, user?.role],
  )

  /**
   * Each card keeps its colour wherever it is dragged: taken from its place in
   * the platform's own order, not from where it sits now.
   */
  const shade = useMemo(
    () => new Map(visibleSkills.map((skill, index) => [skill.id, spectrumColor(index, visibleSkills.length)])),
    [visibleSkills],
  )

  /** Every card in this person's order, and the ones this switch shows. */
  const arranged = useMemo(() => arrangeBy(visibleSkills, savedOrder ?? []), [visibleSkills, savedOrder])
  // Admin is an administrator's alone; anything else remembered falls back to Advance.
  const isAdmin = user?.role === 'admin'
  // What waits in the Review Queue, for its card's corner (2026-10-15).
  const [waiting, setWaiting] = useState(0)
  useEffect(() => {
    if (!isAdmin) return
    readReviewCounts()
      .then((counts) => setWaiting(counts.reported + counts.duplicates))
      .catch(() => setWaiting(0))
  }, [isAdmin])
  // Requests for more allowance, for its card's corner (2026-10-19).
  const [asking, setAsking] = useState(0)
  useEffect(() => {
    if (!isAdmin) return
    readAllowances()
      .then((found) => setAsking(found.requests.filter((one) => !one.resolvedAt).length))
      .catch(() => setAsking(0))
  }, [isAdmin])
  const area: Area = AREAS.some((one) => one.id === chosenArea) && (chosenArea !== 'admin' || isAdmin) ? chosenArea : 'advance'

  /** This person's Starter (2026-10-19): the defaults until they change it. */
  const [starterKept, keepStarter] = useRemembered<string[]>('ww.overview.starter', [DEFAULT_STARTER])
  const starter = useMemo(() => {
    if (!Array.isArray(starterKept) || starterKept[0] === DEFAULT_STARTER) {
      return new Set(visibleSkills.filter((skill) => STARTER_ROUTES.has(skill.route ?? '')).map((skill) => skill.id))
    }
    return new Set(starterKept.filter((id) => typeof id === 'string'))
  }, [starterKept, visibleSkills])
  const addToStarter = (id: string) => keepStarter([...starter, id])
  const takeFromStarter = (id: string) => keepStarter([...starter].filter((one) => one !== id))
  /** The cards Starter could take: Advance's, with somewhere to go, not in it yet. */
  const addable = useMemo(
    () => arranged.filter((skill) => inArea(skill, 'advance', starter) && skill.route && !starter.has(skill.id)),
    [arranged, starter],
  )
  const [picking, setPicking] = useState(false)

  /*
   * Each skill card's colour (Huanrui, 2026-10-21): its edge, chosen from the
   * little dot at its bottom right. The person's own -- kept with the account
   * (useRemembered), the same on every page of cards and every computer.
   * None chosen is the card as it was, in the spectrum's colour.
   */
  const [keptColours, keepColours] = useRemembered<Record<string, string>>('ww.overview.cardColours', {})
  const cardColours = useMemo(() => cleanColours(keptColours), [keptColours])
  const colourCard = (id: string, hex: string | null) => {
    const next = { ...cardColours }
    if (hex) next[id] = hex
    else delete next[id]
    keepColours(next)
  }

  /*
   * Each project card's look (Huanrui, 2026-10-21): a colour, and/or a picture
   * of the person's own as its background. The picture is kept in the
   * project's own pictures (cut to the card's shape first), and the one it
   * replaces is deleted. Which picture and which colour are remembered with
   * the account, by project.
   */
  const [keptLooks, keepLooks] = useRemembered<Record<string, ProjectLook>>('ww.overview.projectLooks', {})
  /*
   * A shared copy arrives with the look it was shared with (card_look,
   * docs/project-card-look.sql, 2026-10-26), shown until this person chooses
   * their own -- which then goes with their settings, as any other.
   */
  const cameWith = useMemo(() => {
    const out: Record<string, ProjectLook> = {}
    for (const project of projects ?? []) {
      const look = cleanLooks({ one: project.card_look }).one
      if (look && (look.colour || look.picture)) out[project.id] = look
    }
    return out
  }, [projects])
  const looks = useMemo(() => ({ ...cameWith, ...cleanLooks(keptLooks) }), [cameWith, keptLooks])
  const cameWithRef = useRef(cameWith)
  cameWithRef.current = cameWith
  const looksRef = useRef(looks)
  looksRef.current = looks
  /** The background pictures' links (signed, two hours), by file id. */
  const [pictureLinks, setPictureLinks] = useState<Record<string, string>>({})
  const [puttingUp, setPuttingUp] = useState<Set<string>>(new Set())
  const setLook = (projectId: string, look: ProjectLook | null) => {
    const next = { ...looksRef.current }
    if (look && (look.colour || look.picture)) next[projectId] = look
    // Taken off a look the copy came with: kept as "none", or it would come back.
    else if (cameWithRef.current[projectId]) next[projectId] = {}
    else delete next[projectId]
    looksRef.current = next
    keepLooks(next)
  }
  const colourProject = (projectId: string, hex: string | null) => {
    // The colour only: the picture stays (it is taken away on its own circle).
    const was = looksRef.current[projectId]
    setLook(projectId, { ...was, colour: hex ?? undefined })
  }
  /** The picture away, from the card and from the project (held 3 s on its circle). */
  const unpictureProject = (projectId: string) => {
    const was = looksRef.current[projectId]
    if (!was?.picture) return
    setLook(projectId, { ...was, picture: undefined })
    void deleteProjectFile(projectId, was.picture).catch(() => undefined)
  }
  // Links for the background pictures, read once a visit from each project
  // that has one (the links are signed and last two hours).
  const asked = useRef(new Set<string>())
  useEffect(() => {
    for (const [projectId, look] of Object.entries(looks)) {
      const fileId = look.picture
      if (!fileId || pictureLinks[fileId] || asked.current.has(fileId)) continue
      asked.current.add(fileId)
      listProjectFiles(projectId, { thumbs: true, copies: false })
        .then((files) => {
          const file = files.find((one) => one.id === fileId)
          if (file) setPictureLinks((was) => ({ ...was, [fileId]: file.thumb || file.url }))
        })
        .catch(() => undefined)
    }
  }, [looks, pictureLinks])

  const pictureProject = (projectId: string, file: File) => {
    setPuttingUp((was) => new Set(was).add(projectId))
    void (async () => {
      try {
        const cut = await cardPicture(file)
        const stored = await uploadProjectFile(projectId, cut, { width: 1280, height: 800 })
        setPictureLinks((was) => ({ ...was, [stored.id]: stored.thumb || stored.url }))
        const before = looksRef.current[projectId]?.picture
        setLook(projectId, { ...looksRef.current[projectId], picture: stored.id })
        if (before && before !== stored.id) void deleteProjectFile(projectId, before).catch(() => undefined)
      } catch (error) {
        console.warn('[WonderWorks] could not put the card picture up', error)
        window.alert(error instanceof Error ? `The picture could not be used: ${error.message}` : 'The picture could not be used.')
      } finally {
        setPuttingUp((was) => {
          const next = new Set(was)
          next.delete(projectId)
          return next
        })
      }
    })()
  }

  const shown = useMemo(() => arranged.filter((skill) => inArea(skill, area, starter)), [arranged, area, starter])
  const byId = useMemo(() => new Map(visibleSkills.map((skill) => [skill.id, skill])), [visibleSkills])

  /**
   * Dragging the cards (Huanrui, 2026-10-02, in place of pinning). Saved
   * straight away; rearranging under one switch keeps every other card where
   * it was.
   */
  const reorderSkills = useCallback(
    (next: string[]) => {
      const whole = mergeSubset(
        arranged.map((skill) => skill.id),
        shown.map((skill) => skill.id),
        next,
      )
      setSavedOrder(whole)
      void saveSkillOrder('home', whole).catch((error: unknown) => console.warn('could not save the order', error))
    },
    [arranged, shown],
  )
  const drag = useDragOrder({ ids: shown.map((skill) => skill.id), onReorder: reorderSkills })

  /**
   * Projects: pinned first, the latest pin first of all; then the rest by when
   * they were last opened (Huanrui, 2026-10-02).
   */
  /** What the projects are searched by (2026-10-26): any part of the name, either case. */
  const [projectQuery, setProjectQuery] = useState('')
  const projectSearch = useRef<HTMLInputElement>(null)
  const orderedProjects = useMemo(() => {
    const rows = projects ?? []
    const pinnedAt = (row: ConversationSummary) => pins.get(pinKey('project', row.id))
    const opened = (row: ConversationSummary) => row.opened_at || row.updated_at || row.created_at || ''
    return [
      ...rows.filter((row) => pinnedAt(row) !== undefined).sort((a, b) => (pinnedAt(b) ?? '').localeCompare(pinnedAt(a) ?? '')),
      ...rows.filter((row) => pinnedAt(row) === undefined).sort((a, b) => opened(b).localeCompare(opened(a))),
    ]
  }, [projects, pins])
  const query = projectQuery.trim().toLowerCase()
  const shownProjects = useMemo(
    () => (query ? orderedProjects.filter((project) => (project.title ?? '').toLowerCase().includes(query)) : orderedProjects),
    [orderedProjects, query],
  )

  /**
   * Delete a conversation.
   *
   * Asked with the browser's own dialog rather than something prettier,
   * deliberately: this removes every message in it and cannot be undone, and a
   * dialog that cannot be missed is the right shape for that. A softer
   * in-page confirmation is a change worth making only if it stays as hard to
   * dismiss by accident.
   */
  const removeProject = useCallback((project: ConversationSummary) => {
    const sure = window.confirm(
      `Delete "${project.title}" and everything said in it?\n\nThis cannot be undone.`,
    )
    if (!sure) return
    const id = project.id
    setProjects((prev) => (prev ?? []).filter((row) => row.id !== id))
    void deleteConversation(id).catch(() => {
      // Put it back rather than leave the person believing it is gone.
      void listConversations().then(setProjects).catch(() => undefined)
    })
  }, [])

  return (
    <div className="overview">
      <section className="overview__hero">
        <GooeyBackground className="overview__hero-bg">
          <div className="overview__hero-inner">
            <ChatLauncher />
          </div>
        </GooeyBackground>
      </section>

      {/* The tab strip sits on the seam between the two halves */}
      <div className="overview__seam">
        <div className="overview__tabs" role="tablist" aria-label="Overview sections">
          {TABS.map((item) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={tab === item.id}
              className={`overview__tab${tab === item.id ? ' overview__tab--active' : ''}`}
              onClick={() => setTab(item.id)}
            >
              {item.label}
            </button>
          ))}
        </div>
        {tab === 'skills' ? (
          <div className="overview__areas" role="group" aria-label="Which tools">
            {AREAS.map((item) => (
              <button
                key={item.id}
                type="button"
                aria-pressed={area === item.id}
                className={`overview__area${area === item.id ? ' overview__area--active' : ''}`}
                onClick={() => setArea(item.id)}
                disabled={item.id === 'admin' && !isAdmin}
                title={item.id === 'admin' && !isAdmin ? 'For administrators only' : undefined}
              >
                {item.label}
                {/* Something waits for an administrator (2026-10-19): a red dot on Admin. */}
                {item.id === 'admin' && isAdmin && waiting + asking > 0 ? (
                  <span className="overview__area-dot" aria-label={`${waiting + asking} waiting`} />
                ) : null}
              </button>
            ))}
          </div>
        ) : (
          /* Projects searched by name (Huanrui, 2026-10-26): where the tools
             have Starter / Advance / Admin, the same size and shape. Typing
             narrows the cards below; Enter opens the first, as a click on it
             would; Escape clears. */
          <label className="overview__search" onClick={() => projectSearch.current?.focus()}>
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <circle cx="7" cy="7" r="4.8" stroke="currentColor" strokeWidth="1.6" />
              <path d="M10.6 10.6L14 14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
            <input
              ref={projectSearch}
              type="search"
              value={projectQuery}
              placeholder="Search projects"
              aria-label="Search projects"
              onChange={(event) => setProjectQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Escape') setProjectQuery('')
                if (event.key === 'Enter' && query && shownProjects[0]) {
                  window.open(`/projects/${shownProjects[0].id}`, '_blank', 'noopener')
                }
              }}
            />
            {projectQuery ? (
              <button
                type="button"
                className="overview__search-clear"
                onClick={() => {
                  setProjectQuery('')
                  projectSearch.current?.focus()
                }}
                aria-label="Clear the search"
                title="Clear"
              >
                <svg width="9" height="9" viewBox="0 0 10 10" aria-hidden="true">
                  <path d="M2 2l6 6M8 2L2 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </button>
            ) : null}
          </label>
        )}
      </div>

      <section className="overview__panel">
        {tab === 'skills' && skillsFailed ? (
          <div className="overview__projects-empty">
            <span className="overview__projects-empty-title">
              The tools could not be loaded
            </span>
            <button type="button" className="ww-btn ww-btn--ghost ww-btn--sm" onClick={reloadSkills}>
              Try again
            </button>
          </div>
        ) : tab === 'skills' ? (
          <div className="overview__grid">
            {/* Drawn once the person's order is known, so the cards do not
                appear in one order and then jump to another. */}
            {allSkills && savedOrder
              ? drag.order.map((id) => {
                  const skill = byId.get(id)
                  if (!skill) return null
                  return (
                    <div key={id} className="overview__slot" {...drag.itemProps(id)}>
                      <SkillTile
                        skill={skill}
                        glow={cardColours[id] ? tripleOf(cardColours[id]) : (shade.get(id) ?? spectrumColor(0, 1))}
                        coloured={Boolean(cardColours[id])}
                        badge={
                          skill.route === '/review' && waiting > 0
                            ? `${waiting} waiting`
                            : skill.route === ALLOWANCES_ROUTE && asking > 0
                              ? `${asking} asking`
                              : undefined
                        }
                      >
                      {/* Inside the card (2026-10-21), so they lean and drift with it. */}
                      {/* Starter is the person's own: a card can be taken off it (2026-10-19). */}
                      {area === 'starter' ? (
                        <button
                          type="button"
                          className="ww-tile__act ww-tile__act--unstar"
                          title="Take this card off Starter (it stays in Advance)"
                          aria-label={`Take ${skill.name} off Starter`}
                          onPointerDown={(event) => event.stopPropagation()}
                          onClick={(event) => {
                            event.preventDefault()
                            event.stopPropagation()
                            takeFromStarter(id)
                          }}
                        >
                          <CloseMark size={10} />
                        </button>
                      ) : null}
                      {/* The card's own colour (2026-10-21), the person's, on every page of cards. */}
                      <CardPaint name={skill.name} colour={cardColours[id]} onColour={(hex) => colourCard(id, hex)} />
                      </SkillTile>
                    </div>
                  )
                })
              : null}
            {/* Add any card from Advance to Starter (2026-10-19). */}
            {area === 'starter' && allSkills && savedOrder ? (
              <div className="overview__slot">
                <button
                  type="button"
                  className="ww-tile overview__addcard"
                  onClick={() => setPicking(true)}
                  disabled={addable.length === 0}
                  title={addable.length === 0 ? 'Every card is already here' : 'Add a card from Advance'}
                >
                  <span className="overview__addcard-plus" aria-hidden="true">+</span>
                  <span className="ww-tile__name">Add a card</span>
                  <span className="ww-tile__summary">Any tool from Advance, here at the start</span>
                </button>
              </div>
            ) : null}
          </div>
        ) : (
          <div className="overview__projects">
            {/* Its own grid, sharing the skills grid's column rule, so the
                first real project card lands exactly where a skill tile would
                rather than starting a second layout. */}
            <div className="overview__projects-grid">
              {/* While searching, only what matches: no New project card. */}
              {query ? null : (
              <Link
                className="project-card project-card--new"
                to="/projects/new"
                target="_blank"
                rel="noopener"
              >
                <span className="project-card__plus" aria-hidden="true">
                  +
                </span>
                <span className="project-card__name">New project</span>
                <span className="project-card__hint">Start from a blank canvas</span>
              </Link>
              )}

              {/* Each keeps its colour while the search narrows the list. */}
              {shownProjects.map((project) => {
                const look = looks[project.id]
                const picture = look?.picture ? pictureLinks[look.picture] : undefined
                return (
                  <div key={project.id} className="overview__slot overview__slot--project">
                    <ProjectTile
                      project={project}
                      glow={look?.colour ? tripleOf(look.colour) : spectrumColor(orderedProjects.indexOf(project), Math.max(orderedProjects.length, 1))}
                      coloured={Boolean(look?.colour)}
                      picture={picture}
                      when={when(project.updated_at)}
                      pinned={pins.has(pinKey('project', project.id))}
                      onTogglePin={() => togglePin(project.id)}
                      onDelete={() => removeProject(project)}
                      onShare={() => setSharing(project)}
                    >
                    <CardPaint
                      name={project.title}
                      colour={look?.colour}
                      onColour={(hex) => colourProject(project.id, hex)}
                      picture={picture}
                      onPicture={(file) => pictureProject(project.id, file)}
                      onRemovePicture={() => unpictureProject(project.id)}
                      busy={puttingUp.has(project.id)}
                      className="card-paint--project"
                    />
                    </ProjectTile>
                  </div>
                )
              })}
            </div>

            {projectsFailed ? (
              <div className="overview__projects-empty">
                <span className="overview__projects-empty-title">
                  Your projects could not be loaded
                </span>
                <button
                  type="button"
                  className="ww-btn ww-btn--ghost ww-btn--sm"
                  onClick={() => setProjectsAttempt((n) => n + 1)}
                >
                  Try again
                </button>
              </div>
            ) : projects !== null && projects.length > 0 && query && shownProjects.length === 0 ? (
              <div className="overview__projects-empty">
                <span className="overview__projects-empty-title">No project called “{projectQuery.trim()}”</span>
              </div>
            ) : projects !== null && projects.length === 0 ? (
              <div className="overview__projects-empty">
                <span className="overview__projects-empty-title">No projects yet</span>
                <span>
                  Say what you want to make in the box above and a project is created for you.
                  Everything a skill generates lands inside it.
                </span>
              </div>
            ) : null}
          </div>
        )}
      </section>

      {picking ? (
        <div className="overview__picker-veil" onClick={() => setPicking(false)}>
          <div className="overview__picker" role="dialog" aria-label="Add a card to Starter" onClick={(event) => event.stopPropagation()}>
            <div className="overview__picker-head">
              <span>Add to Starter</span>
              <button type="button" className="overview__picker-close" onClick={() => setPicking(false)} aria-label="Close">
                <CloseMark size={11} />
              </button>
            </div>
            <div className="overview__picker-list">
              {addable.map((skill) => (
                <button
                  key={skill.id}
                  type="button"
                  className="overview__picker-item"
                  onClick={() => {
                    addToStarter(skill.id)
                    if (addable.length <= 1) setPicking(false)
                  }}
                >
                  <span className="overview__picker-glyph" aria-hidden="true">
                    <SkillGlyph glyph={skill.glyph} />
                  </span>
                  <span className="overview__picker-text">
                    <strong>{skill.name}</strong>
                    <em>{skill.summary}</em>
                  </span>
                  <span className="overview__picker-add">Add</span>
                </button>
              ))}
              {addable.length === 0 ? <p className="overview__picker-empty">Every card is already in Starter.</p> : null}
            </div>
          </div>
        </div>
      ) : null}

      {sharing ? (
        <ShareDialog
          projectId={sharing.id}
          title={sharing.title}
          look={looks[sharing.id] ?? null}
          onClose={() => setSharing(null)}
        />
      ) : null}
    </div>
  )
}
